// What every part of the setup screen shares: the draft, the transaction's
// forms, where the person is (the selected tree node and property tab), the
// deciding columns' values, the editors' catalog and the screen's messages.
// Edits are operations addressed by KEYS (form no, set key, column name) so
// the draft can replay them on a newer server copy (useUdaDraft).

import { createContext, useContext } from 'react'
import type { DesignerCatalog, KitNotifyKind } from '../../designerKit/catalog'
import type { UdaColumn, UdaForm, UdaLayer, UdaSet, UdaTransport } from '../types'
import { emptyForm, type LabelOf, type SetupForm, type SetupMode } from './model'
import type { UdaDraftApi } from './useUdaDraft'
import type { UdaDomains } from './useDomains'

export type SetupNode =
  | { t: 'root' }
  | { t: 'decide'; di?: number }
  | { t: 'combos' }
  | { t: 'set'; set: string }
  | { t: 'col'; set: string; col: string }

export type ColTab = 'general' | 'data' | 'display' | 'behaviour' | 'rules'

export type SetupDialog =
  | { kind: 'preview' }
  | { kind: 'publish' }
  | { kind: 'history' }
  | { kind: 'import' }
  | { kind: 'code'; fnName: string; title?: string }
  | { kind: 'picker'; set: string; rule: number; driver: string }
  | { kind: 'cell'; combo: Record<string, string> }
  | null

export interface SetupCtx {
  mode: SetupMode
  draft: UdaDraftApi
  transport: UdaTransport
  objName: string
  forms: SetupForm[]
  formNo: string
  setFormNo: (f: string) => void
  node: SetupNode
  go: (n: SetupNode) => void
  tab: ColTab
  setTab: (t: ColTab) => void
  domains: UdaDomains
  labelOf: (formNo: string) => LabelOf
  catalog: DesignerCatalog
  notify: (kind: KitNotifyKind, message: string) => void
  dialog: SetupDialog
  openDialog: (d: SetupDialog) => void
  /** Combinations view (Rules / Grid) — kept when stepping away and back. */
  cview: 'rules' | 'grid'
  setCview: (v: 'rules' | 'grid') => void
  /** Whether an addition (set, column, deciding column) is allowed here. */
  additionsAllowed: (formNo: string) => boolean
  /** The host's project name (passed on to the shared editors). */
  project: string
}

export const SetupContext = createContext<SetupCtx | null>(null)

export function useSetup(): SetupCtx {
  const c = useContext(SetupContext)
  if (!c) throw new Error('The additional-attributes setup parts must be inside <UdaSetupScreen>.')
  return c
}

// ── operations (addressed by keys) ─────────────────────────────────────────

export function ensureForm(layer: UdaLayer, formNo: string): UdaForm {
  if (!layer.forms[formNo]) layer.forms[formNo] = emptyForm()
  const f = layer.forms[formNo]
  f.drivers = f.drivers || []
  f.sets = f.sets || []
  f.flags = f.flags || []
  return f
}

export function setIn(layer: UdaLayer, formNo: string, setKey: string): UdaSet {
  const s = ensureForm(layer, formNo).sets.find(x => x.key === setKey)
  if (!s) throw new Error(`The attribute set '${setKey}' is no longer there`)
  s.columns = s.columns || []
  return s
}

export function colIn(layer: UdaLayer, formNo: string, setKey: string, name: string): UdaColumn {
  const c = setIn(layer, formNo, setKey).columns.find(x => x.name === name)
  if (!c) throw new Error(`The column '${name}' is no longer there`)
  return c
}

export const opForm = (formNo: string, fn: (f: UdaForm) => void) => (l: UdaLayer) => fn(ensureForm(l, formNo))
export const opSet = (formNo: string, setKey: string, fn: (s: UdaSet, f: UdaForm) => void) =>
  (l: UdaLayer) => fn(setIn(l, formNo, setKey), ensureForm(l, formNo))
export const opCol = (formNo: string, setKey: string, name: string, fn: (c: UdaColumn, s: UdaSet) => void) =>
  (l: UdaLayer) => fn(colIn(l, formNo, setKey, name), setIn(l, formNo, setKey))
